import React, { useState, useCallback, useEffect } from "react";
import { type NodeProps, type Node, useReactFlow } from "@xyflow/react";
import { FileSpreadsheet, X, Plus, Trash2 } from "lucide-react";
import syntaxHighlight from "../../utils/jsonViewer";
import { WorkflowBaseNode } from "../../common/shared/WorkflowBaseNode";
import { WorkflowNodeModal } from "../../common/shared/WorkflowNodeModal";

export const ExportToExcelNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [reportData, setReportData] = useState<string>(data?.data || "");
  const [mergeData, setMergeData] = useState<string>(data?.merge_data || "");
  const [fileName, setFileName] = useState<string>(data?.file_name || "report.xlsx");
  const [headerMapping, setHeaderMapping] = useState<Record<string, string>>(
    data?.header_mapping || {},
  );
  const [mappingList, setMappingList] = useState<{ key: string; value: string }[]>(
    () => {
      const entries = Object.entries(data?.header_mapping || {});
      if (entries.length === 0) return [{ key: "file_info.name", value: "file_name" }];
      return entries.map(([k, v]) => ({ key: k, value: String(v) }));
    },
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { setNodes } = useReactFlow();

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;
  const isConfigured = !!(reportData && fileName && mappingList.length > 0);

  // ── Sync mappingList -> headerMapping ──────────────────────────────
  useEffect(() => {
    const newMapping: Record<string, string> = {};
    mappingList.forEach((item) => {
      if (item.key) newMapping[item.key] = item.value;
    });
    setHeaderMapping(newMapping);
  }, [mappingList]);

  // ── Sync config to ReactFlow ──────────────────────────────────────
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id !== id
          ? node
          : {
              ...node,
              data: {
                ...node.data,
                data: reportData,
                merge_data: mergeData,
                file_name: fileName,
                header_mapping: headerMapping,
              },
            },
      ),
    );
  }, [reportData, mergeData, fileName, headerMapping, id, setNodes]);

  const handleDrop = (e: React.DragEvent, field: string) => {
    e.preventDefault();
    e.stopPropagation();
    let variable = e.dataTransfer.getData("text/plain");
    if (variable) {
      const normalizedVar = variable.replace(/\.?\[(\d+)\]/g, ".$1");
      let finalVar = normalizedVar;
      if (!finalVar.startsWith("{{")) finalVar = `{{${finalVar}}}`;
      if (field === "data") setReportData(finalVar);
      if (field === "merge_data") setMergeData(finalVar);
    }
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  // ── Mapping list helpers ──────────────────────────────────────────
  const addMappingRow = () => setMappingList([...mappingList, { key: "", value: "" }]);
  const removeMappingRow = (index: number) => {
    const newList = [...mappingList];
    newList.splice(index, 1);
    setMappingList(newList);
  };
  const updateMappingRow = (index: number, field: "key" | "value", val: string) => {
    setMappingList((prev) => {
      const newList = [...prev];
      newList[index] = { ...newList[index], [field]: val };
      return newList;
    });
  };

  // ── Custom outputs sidebar (URL rendering) ────────────────────────
  const renderOutputsSidebar = useCallback(
    () => (
      <div className="w-80 bg-gray-50 overflow-y-auto">
        <div className="p-4">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              Node Outputs
            </h3>
            <p className="text-xs text-gray-500">Available after execution</p>
          </div>
          {hasOutputs ? (
            <div className="space-y-3">
              {Object.entries(data.outputs).map(([key, value]: [string, any]) => (
                <div
                  key={key}
                  className="bg-white border border-gray-200 rounded-lg p-3 hover:border-green-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span className="text-sm font-semibold text-gray-900">{key}</span>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-mono">
                      {typeof value}
                    </span>
                  </div>
                  {key === "url" && typeof value === "string" ? (
                    <a
                      href={value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline break-all font-mono flex items-center gap-1"
                    >
                      {value}
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </a>
                  ) : (
                    <pre
                      className="text-xs font-mono break-all whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: syntaxHighlight(value) }}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600 font-medium mb-1">No report generated</p>
            </div>
          )}
        </div>
      </div>
    ),
    [hasOutputs, data.outputs],
  );

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="Export to Excel"
      icon={<FileSpreadsheet className="w-4 h-4 text-green-700" />}
      nodeDocsType="EXCEL"
      themeColor="green"
      configureLabel="Configure Report"
      isConfigured={isConfigured}
      validationErrors={[
        ...(!reportData ? ["Main data source is required"] : []),
        ...(!fileName ? ["Output file name is required"] : []),
        ...(mappingList.length === 0 ? ["At least one column mapping is required"] : []),
      ]}
      statusText={{
        running: "Generating...",
        success: "Report Generated",
        error: "Generation Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >
      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Export to Excel - Generate reports"
        icon={<FileSpreadsheet className="w-5 h-5 text-green-600" />}
        themeColor="green"
        nodeDocsType="EXCEL"
        nodeId={id}
        validationStatus={{
          isValid: isConfigured,
          errors: [
            ...(!reportData ? ["Main data source is required"] : []),
            ...(!fileName ? ["Output file name is required"] : []),
            ...(mappingList.length === 0 ? ["At least one column mapping is required"] : []),
          ],
        }}
        nodeData={data}
        nodeIcon={<FileSpreadsheet className="w-6 h-6 text-gray-400" />}
        emptyOutputsText="No report generated"
        renderOutputsSidebar={renderOutputsSidebar}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Data Mapping</h3>
        <p className="text-sm text-gray-500 mb-6">
          Configure input sources and column mappings
        </p>

        <div className="space-y-6">
          {/* Main Data Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Main Data Source <span className="text-red-500">*</span>
            </label>
            <div
              onDrop={(e) => handleDrop(e, "data")}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`px-3 py-2 border rounded-lg text-sm flex items-center justify-between ${
                isDraggingOver
                  ? "border-green-500 bg-green-50"
                  : reportData
                    ? "border-green-300 bg-green-50/30"
                    : "border-gray-300 bg-gray-50"
              }`}
            >
              <input
                type="text"
                value={reportData}
                onChange={(e) => setReportData(e.target.value)}
                placeholder="Drag array output here (e.g. {{ForEach.outputs}})"
                className="w-full bg-transparent border-none outline-none text-gray-700 placeholder:text-gray-400"
              />
              {reportData && (
                <button onClick={() => setReportData("")} className="text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {!reportData && (
              <p className="text-xs text-red-500 mt-1">Main data source is required</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Source array to iterate over (drag from For Each or Array node)
            </p>
          </div>

          {/* Merge Data Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Merge Data Source <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <div
              onDrop={(e) => handleDrop(e, "merge_data")}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`px-3 py-2 border rounded-lg text-sm flex items-center justify-between ${
                isDraggingOver
                  ? "border-green-500 bg-green-50"
                  : mergeData
                    ? "border-green-300 bg-green-50/30"
                    : "border-gray-300 bg-gray-50"
              }`}
            >
              <input
                type="text"
                value={mergeData}
                onChange={(e) => setMergeData(e.target.value)}
                placeholder="Drag additional data here"
                className="w-full bg-transparent border-none outline-none text-gray-700 placeholder:text-gray-400"
              />
              {mergeData && (
                <button onClick={() => setMergeData("")} className="text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Additional data array to merge with main data
            </p>
          </div>

          {/* File Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Output File Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="e.g. Payment_Report.xlsx"
              className={`w-full px-3 py-2 border ${!fileName ? "border-red-300" : "border-gray-300"} rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none`}
            />
            {!fileName && (
              <p className="text-xs text-red-500 mt-1">Output file name is required</p>
            )}
          </div>

          {/* Header Mapping */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Column Mapping <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={addMappingRow}
                className="text-xs flex items-center gap-1 text-green-700 hover:underline font-medium"
              >
                <Plus className="w-3 h-3" /> Add Column
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex gap-3 text-xs font-medium text-gray-500">
                <div className="flex-1">Data Field Path</div>
                <div className="flex-1">Excel Column Header</div>
                <div className="w-8" />
              </div>
              <div className="max-h-[250px] overflow-y-auto divide-y divide-gray-100 bg-white">
                {mappingList.length === 0 ? (
                  <div className="p-4 text-center text-gray-400 text-xs italic">
                    No mappings defined. Click "Add Column" to start.
                  </div>
                ) : (
                  mappingList.map((item, index) => (
                    <div key={index} className="flex gap-3 px-3 py-2 items-center">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.key}
                          onChange={(e) => updateMappingRow(index, "key", e.target.value)}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const variable = e.dataTransfer.getData("text/plain");
                            if (variable) {
                              let cleanVar = variable.replace(/\.?\[(\d+)\]/g, ".$1").replace(/^{{|}}$/g, "");
                              if (cleanVar.includes(".outputs.")) {
                                const parts = cleanVar.split(".outputs.");
                                if (parts.length > 1) cleanVar = parts[1];
                              }
                              updateMappingRow(index, "key", cleanVar);
                            }
                            e.currentTarget.classList.remove("border-green-500", "bg-green-50");
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.dataTransfer.dropEffect = "copy";
                            e.currentTarget.classList.add("border-green-500", "bg-green-50");
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.remove("border-green-500", "bg-green-50");
                          }}
                          placeholder="e.g. parse_result.name"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-green-500 outline-none transition-colors"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.value}
                          onChange={(e) => updateMappingRow(index, "value", e.target.value)}
                          placeholder="e.g. Employee Name"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-green-500 outline-none"
                        />
                      </div>
                      <div className="w-8 flex justify-center">
                        <button
                          onClick={() => removeMappingRow(index)}
                          className="text-gray-400 hover:text-red-500 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            {mappingList.length === 0 && (
              <p className="text-xs text-red-500 mt-1">At least one column mapping is required</p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Map nested JSON fields (using dot notation) to Excel column headers. Drag variables from the sidebar to auto-fill fields.
            </p>
          </div>
        </div>
      </WorkflowNodeModal>
    </WorkflowBaseNode>
  );
};

export default ExportToExcelNode;
